import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import { SHARED_CONSTANTS } from '#shared/sharedConstants';
import { formatZodErrors, scrollToFirstError } from '#client/utils/helpers';
import { inventoryTransferService } from '#client/services/inventoryTransferServices';
import { storesService } from '#client/services/storesService';
import { generatePackingSlipPDF } from '#client/pages/Transfers/packingSlipPDF';
import TRANSFER_STRINGS from '#client/pages/Transfers/constants/transfers.en.json';

// A hidden tab never fires requestAnimationFrame, so a frame is raced against
// this. There is nothing to paint in that case, and waiting forever would
// strand the download and leave its button pending.
const PAINT_TIMEOUT_MS = 50;

/** Resolves once the browser has had a chance to paint, or promptly if it cannot. */
const nextFrame = () =>
    new Promise((resolve) => {
        let settled = false;
        const settle = () => {
            if (settled) return;
            settled = true;
            resolve();
        };

        if (typeof requestAnimationFrame === 'function') {
            requestAnimationFrame(settle);
        }

        setTimeout(settle, PAINT_TIMEOUT_MS);
    });

// Initial state for the Transfer store
const initialTransferState = {
    // Transfer Mode State
    mode: 'view',
    transferId: '',
    displayTransferId: '',

    // Transfer Header State
    status: 'Draft',
    type: 'Manual',
    locationFromId: '',
    locationToId: '',
    locationFromStoreNo: null,
    locationToStoreNo: null,
    carrier: '',
    lines: [],
    activityLogs: [],
    lastAdded: {},
    trackingNumber: '',
    entryMode: 'scan-mode',
    dateOfTransfer: '',

    // Pagination State
    pageInfo: {
        hasNextPage: false,
        hasPreviousPage: false,
        startCursor: null,
        endCursor: null,
    },
    currentPage: 1,
    isLoadingMore: false,

    // Toast State
    toastError: '',
    success: '',
    warning: '',
    isLoading: false,
    errors: {},
    isProductsLoading: false,
    // Set for the whole packing slip download, address lookups included, so the
    // header can disable its actions until the file is ready.
    isPackingSlipLoading: false,
    // Original Transfer Data once initialized
    // This is used to revert changes or compare with the current state
    originalTransfer: null,

    skuValue: '',
    barcodeValue: '',
    qtyValue: '1',
    currentUserId: '',
    entryIndex: 0,

    editingValue: null,
    editingIndex: null,
    //const [editingValue, setEditingValue] = useState(null);
    //const [editingIndex, setEditingIndex] = useState(null);
    newTransferModalOpen: false,
};

// Example to colocate stores
// https://zustand.docs.pmnd.rs/guides/flux-inspired-practice#colocate-store-actions

// Create the transfer store with combine middleware for better type inference
const useTransferStoreBase = create(
    combine(initialTransferState, (set, get) => ({
        // Actions for Transfer Mode
        setMode: (mode) => set({ mode }),
        setTransferId: (transferId) => set({ transferId }),
        setDisplayTransferId: (displayTransferId) => set({ displayTransferId }),
        setEntryMode: (entryMode) => set({ entryMode }),

        // Actions for Transfer Header
        setType: (type) => set({ type }),
        setStatus: (status) => set({ status }),
        setCarrier: (carrier) => set({ carrier }),
        setTrackingNumber: (trackingNumber) => set({ trackingNumber }),
        setLocationFromId: (locationFromId) => set({ locationFromId }),
        setLocationToId: (locationToId) => set({ locationToId }),
        setNewTransferModalOpen: (newTransferModalOpen) => set({ newTransferModalOpen }),

        setSkuValue: (skuValue) => set({ skuValue }),
        setBarcodeValue: (barcodeValue) => set({ barcodeValue }),
        setQtyValue: (qtyValue) => set({ qtyValue }),
        resetLines: (lines) => {
            set({ lines: lines });
        },
        setLines: (lines) => {
            const currentLines = get().lines;
            set({ lines: [lines, ...currentLines] });
        },
        removeLine: (index) => {
            const currentLines = get().lines;
            const updatedLines = [...currentLines];
            updatedLines.splice(index, 1);
            set({ lines: updatedLines });
        },
        removeAllLines: () => set({ lines: [] }),
        updateLines: (lines, index) => {
            const currentLines = get().lines;
            const updatedLines = [...currentLines];
            updatedLines[index] = lines;
            set({ lines: updatedLines });
        },
        setActivityLogs: (activityLogs) => {
            const currentActivityLogs = get().activityLogs;
            set({ activityLogs: [activityLogs, ...currentActivityLogs] });
        },
        removeAllActivityLogs: () => set({ activityLogs: [] }),
        setLastAdded: (lastAdded) => {
            set({ lastAdded });
            if (lastAdded) {
                setTimeout(() => get().setLastAdded(null), 500);
            }
        },
        // Actions for Pagination
        setPageInfo: (pageInfo) => set({ pageInfo }),
        setCurrentPage: (currentPage) => set({ currentPage }),
        setIsLoadingMore: (isLoadingMore) => set({ isLoadingMore }),
        setCurrentUserId: (currentUserId) => set({ currentUserId }),
        setEditingValue: (editingValue) => set({ editingValue }),
        setEditingIndex: (editingIndex) => set({ editingIndex }),

        // Actions for Toast State
        setToastError: (toastError) => set({ toastError }),
        setSuccess: (success) => set({ success }),
        setWarning: (warning) => set({ warning }),
        setIsLoading: (isLoading) => set({ isLoading }),
        setIsProductsLoading: (isProductsLoading) => set({ isProductsLoading }),
        setErrors: (errors) => set({ errors }),

        // Initialize Transfer products from server response
        initializeTransferProducts: async (transferId, mode) => {
            // If creating, keep products empty and not loading
            if (!transferId || mode === 'create') {
                set({ isProductsLoading: false });
                return [];
            }

            try {
                const resp = await inventoryTransferService.fetchTransfer(transferId);

                const lines = resp?.data?.lines || [];

                const totalPages = Math.ceil(lines.length / SHARED_CONSTANTS.MAX_PRODUCTS_PER_SHOPIFY_CALL) || 1;

                set({
                    pageInfo: {
                        hasNextPage: totalPages > 1,
                        hasPreviousPage: false,
                        startCursor: null,
                        endCursor: null,
                    },
                    currentPage: 1,
                    productsLoadedAfterRulesChange: true,
                });

                // Also reflect the fetched products on the original Transfer for reset/compare flows
                set((state) => ({
                    originalTransfer: state.originalTransfer
                        ? {
                              ...state.originalTransfer,
                              lines: lines,
                          }
                        : state.originalTransfer,
                }));

                return lines;
            } catch (error) {
                set({ toastError: error?.response?.data?.message || error.message || 'Failed to load transfer products' });
                console.error('Failed to initialize transfer products:', error);
            }
        },

        getLocationFromId: () => {
            return get().locationFromId ? get().locationFromId : localStorage.getItem('activeStore');
        },

        hasPermissionForActionOnTransfer: (locationId, locationIds) => {
            return locationIds.indexOf(locationId) > -1;
        },

        // Complex Actions (Business Logic)
        updateTransferState: (transfer, mode) => {
            set({
                mode: mode || 'view',
                transferId: transfer.id || null,
                displayTransferId: transfer.transferId || null,
                dateOfTransfer: transfer.dateOfTransfer,
                status: transfer.status,
                type: transfer.type,
                locationFromId: transfer.locationIdOrigin,
                locationToId: transfer.locationIdDestination,
                carrier: transfer.shipmentCarrier,
                trackingNumber: transfer.shipmentTrackingNo,
                lines: transfer.lines || [],
            });

            // Keep original transfer data for comparison
            set({ originalTransfer: get().buildOriginalTransferData(transfer) });
        },

        buildTransferData: (mode, status) => {
            const state = get();

            const data = {
                currentUserId: state.currentUserId.toString(),
                status: status ?? state.status,
                lines: state.lines,
                transferId: state.transferId,
                displayTransferId: state.displayTransferId,
                type: state.type,
                locationFromId: state.locationFromId,
                locationToId: state.locationToId,
                carrier: state.carrier,
                trackingNumber: state.trackingNumber,
                dateOfTransfer: state.dateOfTransfer,
            };

            return data;
        },

        buildOriginalTransferData: (originalTransfer) => {
            if (!originalTransfer) {
                return {};
            }

            return {
                ...originalTransfer,
                lines: (originalTransfer.lines || []).map((line) => ({
                    id: line.id,
                    barcode: line.barcode,
                    sku: line.sku,
                    title: line.title,
                    qtyReceived: Number(line.qtyReceived) || 0,
                    qtySent: Number(line.qtySent) || 0,
                    productId: line.productId,
                })),
            };
        },

        // Initialize store with fetched transfer data
        initializeWithTransfer: async (transferId, mode) => {
            if (!transferId) return;

            const currentTransferID = get().transferId;

            // If the current transfer ID matches and mode is 'edit', skip fetching
            // This prevents unnecessary re-fetching when already in edit mode
            if (currentTransferID === Number(transferId) && mode === 'edit') return;

            // Set mode, reset errors and loading state
            set({
                transferId: transferId,
                isLoading: true,
                errors: {},
                mode: mode,
                overridePrices: {},
            });

            try {
                const result = await inventoryTransferService.fetchTransfer(transferId);
                if (!result.success) {
                    throw new Error('Failed to fetch transfer data');
                }

                const transfer = result.data.transfer;

                // Update transfer state
                get().updateTransferState(transfer, mode);
                set({ isLoading: false });
                return transfer;
            } catch (error) {
                set({ toastError: error.message || 'Failed to fetch transfer data' });
                throw error; // Re-throw for external error handling
            }
        },

        // Handle transfer creation
        handleCreateTransfer: async (status, navigate, addToast) => {
            const state = get();
            //if (state.isLoading) return;

            const mode = state.mode;

            set({ isLoading: true, errors: {} });

            try {
                const transferData = get().buildTransferData(mode, status);

                const result = await inventoryTransferService.createTransfer(transferData, get().activityLogs);
                get().removeAllActivityLogs();

                if (!result.success) {
                    if (result.error) {
                        const fieldErrors = formatZodErrors(result.error);
                        set({ errors: fieldErrors });
                        scrollToFirstError();
                    } else {
                        throw new Error(result.message || 'Failed to create Transfer');
                    }
                    return;
                }

                set({ isLoading: false, errors: {}, success: 'Transfer created successfully!' });
                if (navigate) {
                    // Navigate to the newly created transfer view
                    navigate(`/transfers/view/${result.data.transfer.id}`, { replace: true, viewTransition: true });
                }
                // scrroll to top
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } catch (error) {
                if (error.response?.status === 400 && error.response?.data?.error && Array.isArray(error.response.data.error)) {
                    const fieldErrors = formatZodErrors(error.response.data.error);
                    set({ errors: fieldErrors });
                    scrollToFirstError();
                    return;
                }
                const message = error.response?.data?.error?.message || error?.message || 'Failed to create transfer';

                set({ toastError: message });

                addToast?.({ type: 'error', message });
            } finally {
                set({ isLoading: false });
            }
        },

        // Handle transfer update
        handleUpdateTransfer: async (transferId, navigate, updates, addToast) => {
            set({ isLoading: true, errors: {} });
            try {
                const transferData = get().buildTransferData('edit');

                if (updates) {
                    Object.assign(transferData, updates);
                }

                // The shipping store's `available` count is only worth checking while this
                // transfer's units are still counted in it. Initiating the transfer moves them
                // into `reserved`, so re-checking `available` on any later status change
                // (receive, exception, cancel) or on a re-save compares against a count this
                // transfer already reduced, and blocks a legitimate update. Mirrors the
                // server's `firstInitiated` condition - the point at which the reserve is taken.
                const isFirstInitiation = transferData.status === 'Initiated' && get().originalTransfer?.status !== 'Initiated';

                if (isFirstInitiation) {
                    let transferLines = get().lines;
                    let variantIdToQuantityMap = new Map();
                    for (let line of transferLines) {
                        variantIdToQuantityMap.set(line.productId, {
                            quantity: Number(line.qtySent),
                            title: line.title,
                            sku: line.sku,
                        });
                    }

                    const resp = await inventoryTransferService.fetchBulkInventoryByVariantIds(Array.from(variantIdToQuantityMap.keys()), get().getLocationFromId());

                    let respMap = new Map();
                    for (let item of resp.data.variantIdAndQuantity) {
                        respMap.set(item.id, Number(item.quantity));
                    }

                    let linesThatExceedInventory = [];

                    for (let [variantId, lineData] of variantIdToQuantityMap) {
                        // Skip only when the lookup returned nothing for this variant. A
                        // legitimate 0 has to reach the comparison below, otherwise a store
                        // with nothing available passes validation and goes negative.
                        if (!respMap.has(variantId)) {
                            continue;
                        }

                        const availableShopifyInventory = Number(respMap.get(variantId));

                        if (Number.isNaN(availableShopifyInventory)) {
                            continue;
                        }

                        if (availableShopifyInventory < lineData.quantity) {
                            linesThatExceedInventory.push({
                                variantId,
                                title: lineData.title,
                                requestedQty: lineData.quantity,
                                availableShopifyInventory: availableShopifyInventory,
                                sku: lineData.sku,
                            });
                        }
                    }

                    if (linesThatExceedInventory.length > 0) {
                        addToast({
                            type: 'error',
                            message: `Quantity requested for ${linesThatExceedInventory.map((line) => line.title).join(', ')} exceeds shipping locations inventory`,
                        });
                        return;
                    }
                }

                const result = await inventoryTransferService.updateTransfer(transferId, transferData, get().originalTransfer, get().activityLogs);
                get().removeAllActivityLogs();

                if (!result.success) {
                    if (result.error) {
                        set({ errors: formatZodErrors(result.error) });
                        scrollToFirstError();
                        return;
                    }
                    throw new Error(result.message || 'Failed to update transfer');
                }

                set({ isLoading: false, errors: {}, success: result.message || 'Transfer updated successfully!' });

                if (navigate) {
                    if (window.location.pathname.includes('/view')) {
                        navigate(0);
                    } else {
                        navigate(`/transfers/view/${transferId}`, { viewTransition: true });
                    }
                }
                // Scroll to top after successful update
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } catch (error) {
                if (error.response?.status === 400 && Array.isArray(error.response?.data?.error)) {
                    set({ errors: formatZodErrors(error.response.data.error) });
                    scrollToFirstError();
                    return;
                }
                const message = error.response?.data?.error?.message || error?.message || 'Failed to update transfer';

                set({ toastError: message });

                addToast?.({ type: 'error', message });
            } finally {
                set({ isLoading: false });
            }
        },

        // Handle transfer cancellation
        handleCancelClick: async (navigate) => {
            const mode = get().mode;
            const transferId = get().transferId;

            const navigateUrl = mode === 'create' || mode == 'view' ? '/transfers' : `/transfers/view/${transferId}`;
            if (navigate) {
                navigate(navigateUrl, { viewTransition: true });
            }
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        },

        // Handle transfer cancellation
        handleCancelTransfer: async (navigate, addToast) => {
            const mode = get().mode;
            const transferId = get().transferId;

            if (mode === 'create') {
                if (navigate) {
                    navigate('/transfers', { viewTransition: true });
                }
                // Scroll to top after cancellation
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                try {
                    const transferData = get().buildTransferData('edit', 'Canceled');

                    const result = await inventoryTransferService.updateTransfer(transferId, transferData, get().originalTransfer);
                    if (!result.success) {
                        if (result.error) {
                            set({ errors: formatZodErrors(result.error) });
                            scrollToFirstError();
                            return;
                        }
                        throw new Error(result.message || 'Failed to update transfer');
                    }

                    set({ isLoading: false, errors: {}, success: result.message || 'Transfer canceled successfully!' });

                    if (navigate) {
                        if (window.location.pathname.includes('/view')) {
                            navigate(0);
                        } else {
                            navigate(`/transfers/view/${transferId}`, { replace: true, viewTransition: true });
                        }
                    }
                    // Scroll to top after successful update
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                    return;
                } catch (error) {
                    if (error.response?.status === 400 && Array.isArray(error.response?.data?.error)) {
                        set({ errors: formatZodErrors(error.response.data.error) });
                        scrollToFirstError();
                        return;
                    }
                    set({ toastError: error.message || 'Failed to update transfer' });
                    addToast({
                        type: 'error',
                        message: error.response?.data?.error.message || error?.message || 'Failed to update transfer',
                    });
                } finally {
                    set({ isLoading: false });
                }
            }
        },

        handleDeleteLine: (index) => {
            set((state) => {
                const updatedLines = [...state.lines];
                updatedLines.splice(index, 1);

                return {
                    ...state,
                    lines: updatedLines,
                };
            });
        },

        checkTransferForExceptions: async (navigate, addToast) => {
            const lines = get().lines;

            let hasOverage = false;
            for (let x = 0; x < lines.length; x++) {
                const line = lines[x];
                if (line.qtyReceived > (line.qtySent ?? 0)) {
                    hasOverage = true;
                }
            }

            if (hasOverage) {
                get().setNewTransferModalOpen(true);
            } else {
                await get().handleInitiateTransfer(null, navigate, addToast);
            }
        },

        handlePackingSlip: async (addToast, clearToasts) => {
            set({ isPackingSlipLoading: true });

            // Same announce-then-replace sequence the cycle count export uses, so
            // the two long-running downloads report progress the same way.
            clearToasts?.();
            addToast?.({ message: 'Preparing packing slip...', type: 'info' });

            try {
                const transferData = get().buildTransferData('edit');

                const [locationFrom, locationTo] = await Promise.all([storesService.fetchStore(transferData.locationFromId), storesService.fetchStore(transferData.locationToId)]);

                transferData.locationFrom = locationFrom;
                transferData.locationTo = locationTo;

                transferData.totalQty = transferData.lines.reduce((qty, line) => qty + (line.qtySent ?? 0), 0);

                // Generation is synchronous and holds the main thread. Wait for a
                // frame so the pending state is on screen before it does, otherwise
                // a cached address lookup leaves the click looking like a no-op.
                await nextFrame();

                await generatePackingSlipPDF([transferData]);

                clearToasts?.();
                addToast?.({ message: 'Packing slip downloaded successfully', type: 'success' });
            } catch (error) {
                const message = error?.response?.data?.message || error?.message || 'Failed to build the packing slip';
                set({ toastError: message });
                clearToasts?.();
                addToast?.({ type: 'error', message });
            } finally {
                set({ isPackingSlipLoading: false });
            }
        },

        handleNewTransferOverages: async (navigate, addToast) => {
            try {
                const transferData = {
                    currentUserId: get().currentUserId.toString(),
                    status: 'Draft',
                    lines: [],
                    type: 'Automatic',
                    locationFromId: get().locationFromId,
                    dateOfTransfer: get().dateOfTransfer,
                    locationToId: get().locationToId,
                    carrier: get().carrier,
                    trackingNumber: get().trackingNumber,
                };

                const lines = get().lines;
                for (let x = 0; x < lines.length; x++) {
                    const line = lines[x];
                    if (line.qtyReceived > (line.qtySent ?? 0)) {
                        const newLine = { sku: line.sku, qtySent: line.qtyReceived - (line.qtySent ?? 0), productId: line.productId, title: line.title };
                        line.qtyReceived = line.qtySent;
                        transferData.lines.push(newLine);
                    }
                    //remove line from current transfer if it was newly added during receive, cannot update a line that doesn't exist and it's been added to the new transfer
                    if (line.id < 0) {
                        get().removeLine(line);
                    }
                }

                const result = await inventoryTransferService.createTransfer(transferData);

                // update current transfer
                const originalTransferData = get().buildTransferData('edit');
                //mark original transfer as Complete
                originalTransferData.status = (await get().isStatusException(get().lines)) ? 'Exception' : 'Complete';
                await inventoryTransferService.updateTransfer(get().transferId, originalTransferData, get().originalTransfer, get().activityLogs);

                await inventoryTransferService.linkOverageTranfers(result.data.transfer, originalTransferData, get().currentUserId.toString());

                set({ isLoading: false, errors: {}, success: 'Transfer created successfully!' });
                if (navigate) {
                    // Navigate to the newly created transfer view
                    navigate(`/transfers/view/${get().transferId}`, { viewTransition: true });
                }
                // scrroll to top
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } catch (error) {
                addToast({
                    type: 'error',
                    message: error.response?.data?.message || error.response?.data?.error || 'Something went wrong trying to create new transfer',
                });
            } finally {
                get().setNewTransferModalOpen(false);
            }
        },

        // Handle transfer submission (create or update)
        handleInitiateTransfer: async (status, navigate, addToast) => {
            const mode = get().mode;
            const transferId = get().transferId;

            set({
                errors: {},
                toastError: '',
                success: '',
                warning: '',
            });

            if (mode === 'create') {
                await get().handleCreateTransfer(status, navigate, addToast);
            } else {
                if (mode == 'receive') {
                    status = (await get().isStatusException(get().lines)) ? 'Exception' : 'Complete';
                }

                let updates = { status: status };
                await get().handleUpdateTransfer(transferId, navigate, updates, addToast);
            }
        },

        isStatusException: async (lines) => {
            for (let x = 0; x < lines.length; x++) {
                const line = lines[x];
                if (line.qtyReceived < line.qtySent) {
                    return true;
                }
            }

            return false;
        },

        handleDeleteItem: async (index) => {
            const line = get().lines[index];
            if (get().mode == 'receive' && line.qtySent > 0) {
                line.qtyReceived = 0;

                await get().addActivityLog('DELETE', `CLEARED received values for ${line.title})`);
            } else {
                get().lines.splice(index, 1);

                await get().addActivityLog('DELETE', `REMOVED ${line.title}) from items list`);
            }
            get().updateLines(get().lines);
        },

        handleSaveDraftTransfer: async (navigate) => {
            set({
                errors: {},
                toastError: '',
                success: '',
                warning: '',
            });

            await get().handleCreateTransfer('Draft', navigate);
        },

        handleShipTransfer: async (navigate, addToast) => {
            set({ isLoading: true, errors: {} });

            try {
                const transferData = get().buildTransferData('edit');

                transferData.status = 'In Transit';
                transferData.dateOfTransfer = new Date();

                const result = await inventoryTransferService.updateTransfer(get().transferId, transferData, get().originalTransfer);

                if (!result.success) {
                    if (result.error) {
                        set({ errors: formatZodErrors(result.error) });
                        scrollToFirstError();
                        return;
                    }
                    throw new Error(result.message || 'Failed to update transfer');
                }

                set({ isLoading: false, errors: {}, success: result.message || 'Transfer updated successfully!' });
                if (navigate) {
                    navigate(0);
                }
                // Scroll to top after successful update
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } catch (error) {
                if (error.response?.status === 400 && Array.isArray(error.response?.data?.error)) {
                    set({ errors: formatZodErrors(error.response.data.error) });
                    scrollToFirstError();
                    return;
                }
                set({ toastError: error.message || 'Failed to update transfer' });
                addToast({
                    type: 'error',
                    message: error.response?.data?.error.message || error?.message || 'Failed to update transfer',
                });
            } finally {
                set({ isLoading: false });
            }
        },

        addActivityLog: async (action, message) => {
            const logdata = {
                staffId: get().currentUserId,
                objectType: 'InventoryTransfer',
                objectId: get().displayTransferId,
                action: action,
                message: message,
                timestamp: new Date(),
            };

            get().setActivityLogs(logdata);
        },

        processLineItemEntry: async (skuValue, barcodeValue, qtyValue, addToast) => {
            const mode = get().mode;

            get().entryIndex--;

            // Validate that at least one input is provided
            if (!barcodeValue && !skuValue) {
                addToast({
                    type: 'error',
                    message: TRANSFER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.MISSING_SKU_OR_BARCODE_ERROR,
                });
                return;
            }

            // Validate that at least one input is provided
            if (qtyValue <= 0) {
                addToast({
                    type: 'error',
                    message: 'Negative quantities cannot be added',
                });
                return;
            }

            // Validate entered quantity is a number
            if (isNaN(qtyValue)) {
                addToast({
                    type: 'error',
                    message: 'Quantity must be a number',
                });
                return;
            }

            const variant = await inventoryTransferService.checkProduct(skuValue, barcodeValue);
            if (!variant.data.variant) {
                addToast({
                    type: 'error',
                    message: `The SKU or Barcode you entered, ${skuValue ? skuValue : barcodeValue}, is invalid. Please enter a new entry`,
                });

                get().setBarcodeValue('');
                get().setSkuValue('');
                get().setQtyValue('1');
                return;
            }

            if (barcodeValue && !skuValue) {
                skuValue = variant.data.variant.sku;
            }

            let line = null;
            let lines = null;
            try {
                // Process based on receiving mode
                const qtyToAdd = Number(qtyValue);

                if (mode == 'create' || mode == 'edit') {
                    const resp = await inventoryTransferService.fetchInventoryByStoreLocation(variant.data.variant.id, get().getLocationFromId());

                    lines = get().lines;
                    line = lines.find((l) => {
                        return l.sku == skuValue;
                    });
                    let qtyToCheck = qtyToAdd;
                    if (line) {
                        qtyToCheck = line.qtySent + qtyToAdd;
                    }
                    if (qtyToCheck > resp.data.quantity) {
                        addToast({
                            type: 'error',
                            message: `Quantity requested for ${variant.data.variant.product.title} (${variant.data.variant.title}) exceeds shipping locations inventory`,
                        });

                        return;
                    }

                    // if sku exists, then append the quantity to the line
                    if (line) {
                        line.qtySent += qtyToAdd;
                        get().setLastAdded(line);
                        get().updateLines(lines);
                    } else {
                        const newLine = {
                            id: get().entryIndex,
                            sku: skuValue,
                            barcode: variant.data.variant.barcode,
                            qtySent: qtyToAdd,
                            qtyReceived: 0,
                            itemInventoryId: resp.data.itemInventoryId,
                            productId: variant.data.variant.id,
                            title: `${variant.data.variant.product.title} (${variant.data.variant.title})`,
                        };

                        get().setLines(newLine);
                        get().setLastAdded(newLine);
                    }

                    addToast({
                        type: 'success',
                        message: `Successfully added ${variant.data.variant.product.title} (${variant.data.variant.title}) to items list`,
                    });

                    await get().addActivityLog('SCAN', `ADDED ${qtyToAdd}x ${variant.data.variant.product.title} (${variant.data.variant.title}) to items list`);
                } else if (mode == 'receive') {
                    const resp = await inventoryTransferService.fetchInventoryByStoreLocation(variant.data.variant.id, get().locationToId);
                    let found = false;
                    lines = get().lines;
                    line = lines.find((l) => {
                        return l.sku == skuValue;
                    });

                    if (line) {
                        line.qtyReceived += qtyToAdd;
                        line.itemInventoryId = resp.data.itemInventoryId;

                        get().setLastAdded(line);
                        found = true;
                    }

                    await get().addActivityLog('SCAN', `RECEIVED ${qtyToAdd}x ${variant.data.variant.product.title} (${variant.data.variant.title})`);

                    if (!found) {
                        const newLine = {
                            id: get().entryIndex,
                            sku: skuValue,
                            barcode: variant.data.variant.barcode,
                            productId: variant.data.variant.id,
                            title: `${variant.data.variant.product.title} (${variant.data.variant.title})`,
                            qtyReceived: qtyToAdd,
                            itemInventoryId: resp.data.itemInventoryId,
                        };
                        get().setLastAdded(newLine);
                        get().setLines(newLine);
                    } else {
                        get().updateLines(lines);
                    }
                }

                // Clear inputs on success
                get().setBarcodeValue('');
                get().setSkuValue('');
                get().setQtyValue('1');
            } catch (error) {
                // Set appropriate error message based on mode
                const errorMessage = TRANSFER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.MISSING_SKU_OR_BARCODE_ERROR;

                addToast({
                    type: 'error',
                    message: error?.message || errorMessage,
                });
            }
        },

        handleScannedSubmit: async (addToast, barcodeValue) => {
            await get().processLineItemEntry(null, barcodeValue, 1, addToast);
        },

        handleManualSubmit: async (addToast) => {
            const barcodeValue = get().barcodeValue;
            let skuValue = get().skuValue;
            const qtyValue = get().qtyValue;

            await get().processLineItemEntry(skuValue, barcodeValue, qtyValue, addToast);
        },

        // Reset store to initial state
        reset: () => {
            set(initialTransferState);
        },

        // Reset only form data, keep UI state
        resetForm: async (mode) => {
            const currentState = get();
            set({
                ...initialTransferState,
                // Keep UI state
                toastError: currentState.toastError,
                success: currentState.success,
                warning: currentState.warning,
                isLoading: currentState.isLoading,
                errors: currentState.errors,
            });

            if (mode === 'create') {
                set({
                    mode: mode,
                    transferId: null,
                });
            }

            return;
        },

        handleEditItem: async (index, line) => {
            const mode = get().mode;
            get().setEditingValue(mode == 'edit' || mode == 'create' ? line.qtySent : line.qtyReceived);
            get().setEditingIndex(index);
        },

        handleCancelEdit: async () => {
            get().setEditingIndex(null);
            get().setEditingValue(null);
        },

        handleManualUpdateOfTotal: async (index, line, addToast) => {
            const mode = get().mode;
            const resp = await inventoryTransferService.fetchInventoryByStoreLocationAndSku(line.sku, get().locationFromId);
            if ((mode == 'edit' || mode == 'create') && get().editingValue > resp.data.quantity) {
                addToast({
                    type: 'error',
                    message: `Quantity requested for ${line.title} exceeds shipping locations inventory`,
                });

                return;
            }

            const prevValue = mode == 'edit' || mode == 'create' ? line.qtySent : line.qtyReceived;
            const curValue = get().editingValue;

            var difference = curValue - prevValue;

            if ((mode == 'edit' || mode == 'create') && difference > 0) {
                await get().addActivityLog('SCAN', `ADDED ${difference}x ${line.title}) to items list`);
            } else if ((mode == 'edit' || mode == 'create') && difference < 0) {
                await get().addActivityLog('DELETE', `REMOVED ${difference * -1}x ${line.title}) from items list`);
            } else if (mode == 'receive' && difference > 0) {
                await get().addActivityLog('SCAN', `RECEIVED ${difference}x ${line.title}) by edit`);
            } else if (mode == 'receive' && difference < 0) {
                await get().addActivityLog('DELETE', `REDUCED ${difference * -1}x ${line.title}) from received list by edit`);
            }

            if (mode == 'edit' || mode == 'create') {
                line.qtySent = parseInt(get().editingValue);
            } else {
                line.qtyReceived = parseInt(get().editingValue);
            }
            get().setEditingValue(null);
            get().setEditingIndex(null);
        },

        getEditingValue: (index, line) => {
            const mode = get().mode;
            return get().editingValue ?? (mode == 'create' || mode == 'edit' ? line.qtySent : line.qtyReceived);
        },

        handleQtyChange: async (index, value) => {
            get().setEditingValue(value);
        },

        isValidQty: (index, entry) => {
            const value = String(get().getEditingValue(index, entry)).trim();
            return value !== '' && !isNaN(Number(value)) && parseInt(value) > 0;
        },
        canTransfer: () => {
            // if all the lines status is 'ARCHIVED' then return false else true
            const lines = get().lines;
            if (lines.length === 0) return false;

            for (let x = 0; x < lines.length; x++) {
                const line = lines[x];
                if (line.status !== 'ARCHIVED') {
                    return true;
                }
            }

            return false;
        },
    }))
);

// Auto-generated selectors function
// https://zustand.docs.pmnd.rs/guides/auto-generating-selectors
const createSelectors = (_store) => {
    let store = _store;
    store.use = {};

    for (let k of Object.keys(store.getState())) {
        store.use[k] = () => store((s) => s[k]);
    }

    return store;
};

// Create the store with auto-generated selectors
export const useTransferStore = createSelectors(useTransferStoreBase);

// Example usage of the auto-generated selectors:
// const name = useTransferStore.use.name();
// const mode = useTransferStore.use.mode();
// const isLoading = useTransferStore.use.isLoading();

// Or you can use them directly:
// export const useName = () => useTransferStore.use.name();
// export const useMode = () => useTransferStore.use.mode();
// export const useIsLoading = () => useTransferStore.use.isLoading();

// This is an example of a store with no actions
// https://zustand.docs.pmnd.rs/guides/practice-with-no-store-actions
//export const setMode = (mode) => useTransferStore.setState({ mode });

/**
 * Selector hooks for optimized component rendering
 * Each hook uses useShallow to prevent unnecessary re-renders
 */

// Selector for transfer actions, used in components that need to perform actions on the transfer
// This includes actions like archiving, setting various transfer properties, and handling transfer creation and updates
export const useTransferActions = () =>
    useTransferStore(
        useShallow((state) => ({
            setStatus: state.setStatus,
            setMode: state.setMode,
            setLines: state.setLines,
            removeLine: state.removeLine,
            removeAllLines: state.removeAllLines,
            updateLines: state.updateLines,
            setToastError: state.setToastError,
            setSuccess: state.setSuccess,
            setWarning: state.setWarning,
            setIsLoading: state.setIsLoading,
            setIsProductsLoading: state.setIsProductsLoading,
            setErrors: state.setErrors,
            setNotificationThreshold: state.setNotificationThreshold,
            setExternallyManaged: state.setExternallyManaged,
            setFirstNameRequired: state.setFirstNameRequired,
            setLastNameRequired: state.setLastNameRequired,
            setEmployeeIdRequired: state.setEmployeeIdRequired,
            setOverridePrices: state.setOverridePrices,
            setPoNumberRequired: state.setPoNumberRequired,
            setTransferDescription: state.setTransferDescription,
            setSortSelected: state.setSortSelected,
            updateTransferState: state.updateTransferState,
            buildTransferData: state.buildTransferData,
            buildOriginalTransferData: state.buildOriginalTransferData,
            handleCreateTransfer: state.handleCreateTransfer,
            handleEligibleProductRulesChange: state.handleEligibleProductRulesChange,
            handleUpdateTransfer: state.handleUpdateTransfer,
            handleCancelTransfer: state.handleCancelTransfer,
            handleCancelClick: state.handleCancelClick,
            handlePageChange: state.handlePageChange,
            handleTransferSubmit: state.handleTransferSubmit,
            handleDeleteItem: state.handleDeleteItem,
            reset: state.reset,
            resetForm: state.resetForm,
            initializeWithTransfer: state.initializeWithTransfer,
            setEditingIndex: state.setEditingIndex,
        }))
    );

// Selector for transfer header information, used in components that need to display basic transfer details
export const useTransferHeader = () =>
    useTransferStore(
        useShallow((state) => ({
            mode: state.mode,
            transferId: state.transferId,
            displayTransferId: state.displayTransferId,
            name: state.name,
            type: state.type,
            locationFromId: state.locationFromId,
            locationToId: state.locationToId,
            status: state.status,
            lines: state.lines,
            carrier: state.carrier,
            trackingNumber: state.trackingNumber,
            originalTransfer: state.originalTransfer,
            newTransferModalOpen: state.newTransferModalOpen,
            checkTransferForExceptions: state.checkTransferForExceptions,
            editingIndex: state.editingIndex,

            hasPermissionForActionOnTransfer: state.hasPermissionForActionOnTransfer,
            handleInitiateTransfer: state.handleInitiateTransfer,
            handleCancelTransfer: state.handleCancelTransfer,
            handleCancelClick: state.handleCancelClick,
            handleSaveDraftTransfer: state.handleSaveDraftTransfer,
            handleShipTransfer: state.handleShipTransfer,
            handleNewTransferOverages: state.handleNewTransferOverages,
            handlePackingSlip: state.handlePackingSlip,
            isPackingSlipLoading: state.isPackingSlipLoading,
            setLocationToId: state.setLocationToId,
            setLocationFromId: state.setLocationFromId,
            setTrackingNumber: state.setTrackingNumber,
            setCarrier: state.setCarrier,
            resetLines: state.resetLines,
            setNewTransferModalOpen: state.setNewTransferModalOpen,
            canTransfer: state.canTransfer,
        }))
    );

// Selector for transfer information, used in components that need to access basic transfer details
export const useTransferInfo = () =>
    useTransferStore(
        useShallow((state) => ({
            mode: state.mode,
            errors: state.errors,
        }))
    );

// Selector for product table data
export const useTransferTable = () =>
    useTransferStore(
        useShallow((state) => ({
            editingIndex: state.editingIndex,
            editingValue: state.editingValue,
            mode: state.mode,
            type: state.type,
            status: state.status,
            currentPage: state.currentPage,
            lines: state.lines,
            isLoadingMore: state.isLoadingMore,
            errors: state.errors,
            sortSelected: state.sortSelected,
            transferId: state.transferId,
            isProductsLoading: state.isProductsLoading,
            lastAdded: state.lastAdded,
            setLastAdded: state.setLastAdded,
            isLoading: state.isLoading,
            handleDeleteItem: state.handleDeleteItem,
            locationFromId: state.locationFromId,
            handleEditItem: state.handleEditItem,
            handleCancelEdit: state.handleCancelEdit,
            handleManualUpdateOfTotal: state.handleManualUpdateOfTotal,
            getEditingValue: state.getEditingValue,
            handleQtyChange: state.handleQtyChange,
            isValidQty: state.isValidQty,
        }))
    );

// Selector for transfer toast messages, used in components that need to display toast notifications
// Get toast messages and actions to set them
export const useTransferToast = () =>
    useTransferStore(
        useShallow((state) => ({
            toastError: state.toastError,
            success: state.success,
            warning: state.warning,
            errors: state.errors,

            // Actions
            setToastError: state.setToastError,
            setSuccess: state.setSuccess,
            setWarning: state.setWarning,
        }))
    );

// Selector for transfer mode, used in components that need to know the current mode (create/edit/view) and can be used to set it
export const useTransferMode = () =>
    useTransferStore(
        useShallow((state) => ({
            mode: state.mode,
            setMode: state.setMode,
        }))
    );

export const useTransferEntry = () =>
    useTransferStore(
        useShallow((state) => ({
            mode: state.mode,
            setMode: state.setMode,

            skuValue: state.skuValue,
            barcodeValue: state.barcodeValue,
            qtyValue: state.qtyValue,
            entryMode: state.entryMode,

            setSkuValue: state.setSkuValue,
            setQtyValue: state.setQtyValue,
            setBarcodeValue: state.setBarcodeValue,
            setLines: state.setLines,

            handleManualSubmit: state.handleManualSubmit,
            handleScannedSubmit: state.handleScannedSubmit,
            setLastAdded: state.setLastAdded,

            setFieldErrors: state.setFieldErrors,
            setToastError: state.setToastError,
            setSuccess: state.setSuccess,
            setWarning: state.setWarning,
            setEntryMode: state.setEntryMode,
        }))
    );
